export const SOURCE_LOCALE = 'en';
/**
 * The one place a language is added. Everything else about a locale — its type, the list, its
 * label, the code each translation vendor wants — derives from an entry here, so a new language
 * is a single entry rather than a hunt through the adapters.
 *
 * `deepl` is the vendor's own code because DeepL rejects the bare tags we store (`es`, `pt`, `zh`).
 * It is validated against the DeepL SDK's union inside the DeepL provider, which is where that
 * knowledge belongs; this file only carries the string. pdf2zh needs no such entry — it accepts
 * these tags verbatim and keys its font and line-height tables off them directly.
 *
 * The caption fields serve live/recorded caption translation: `captionLanguage` is the BCP 47 tag
 * for the HLS subtitle track, `nativeLabel` names it in the player's CC menu, and `promptLanguage`
 * is how the language is named to the translation model.
 */
export const APP_LOCALES = {
    'es-us': {
        label: 'Spanish',
        deepl: 'es-419',
        captionLanguage: 'es-US',
        nativeLabel: 'Español',
        promptLanguage: 'Latin American Spanish',
    },
    zh: {
        label: 'Chinese (Simplified)',
        deepl: 'zh-HANS',
        captionLanguage: 'zh-Hans',
        nativeLabel: '简体中文',
        promptLanguage: 'Simplified Chinese',
    },
    'zh-hant': {
        label: 'Chinese (Traditional)',
        deepl: 'zh-HANT',
        captionLanguage: 'zh-Hant',
        nativeLabel: '繁體中文',
        promptLanguage: 'Traditional Chinese',
    },
    vi: {
        label: 'Vietnamese',
        deepl: 'vi',
        captionLanguage: 'vi',
        nativeLabel: 'Tiếng Việt',
        promptLanguage: 'Vietnamese',
    },
    tl: {
        label: 'Tagalog (Filipino)',
        deepl: 'tl',
        captionLanguage: 'fil',
        nativeLabel: 'Tagalog',
        promptLanguage: 'Tagalog (Filipino)',
    },
    ar: {
        label: 'Arabic',
        deepl: 'ar',
        captionLanguage: 'ar',
        nativeLabel: 'العربية',
        promptLanguage: 'Modern Standard Arabic',
    },
    fr: {
        label: 'French',
        deepl: 'fr',
        captionLanguage: 'fr',
        nativeLabel: 'Français',
        promptLanguage: 'French',
    },
    ko: {
        label: 'Korean',
        deepl: 'ko',
        captionLanguage: 'ko',
        nativeLabel: '한국어',
        promptLanguage: 'Korean',
    },
    ru: {
        label: 'Russian',
        deepl: 'ru',
        captionLanguage: 'ru',
        nativeLabel: 'Русский',
        promptLanguage: 'Russian',
    },
    pt: {
        label: 'Portuguese',
        deepl: 'pt-BR',
        captionLanguage: 'pt-BR',
        nativeLabel: 'Português',
        promptLanguage: 'Brazilian Portuguese',
    },
    de: {
        label: 'German',
        deepl: 'de',
        captionLanguage: 'de',
        nativeLabel: 'Deutsch',
        promptLanguage: 'German',
    },
    it: {
        label: 'Italian',
        deepl: 'it',
        captionLanguage: 'it',
        nativeLabel: 'Italiano',
        promptLanguage: 'Italian',
    },
};
export const TARGET_APP_LOCALES = Object.keys(APP_LOCALES);
export const SUPPORTED_APP_LOCALES = [SOURCE_LOCALE, ...TARGET_APP_LOCALES];
export const APP_LOCALE_LABELS = Object.fromEntries(Object.entries(APP_LOCALES).map(([locale, { label }]) => [locale, label]));
export const isTargetAppLocale = (value) => typeof value === 'string' && Object.hasOwn(APP_LOCALES, value);
export const filterTargetAppLocales = (values) => values.filter(isTargetAppLocale);
